import {HeroHeader} from "@/components/HeroHeader";
import {type Competition, useCompetitions} from "@/hooks/useCompetitions";
import LoadingGridItems from "@/components/LoadingGridItems";
import NotAvailable from "@/components/NotAvailable";
import CompetitionDetailsDialog from "@/features/competitions/components/CompetitionDetailsDialog";
import {useQueryModal} from "@/hooks/useQueryModal";
import {useMemo} from "react";
import CompetitionCard from "@/features/competitions/components/CompetitionCard.tsx";
import {MetaTags} from "@/hooks/MetaTags.tsx";

export default function PublicCompetitionPage() {
    const {searchParams, setParam, closeModal} = useQueryModal();

    const {
        data: result = {page: 0, size: 0, total: 0, items: []},
        isLoading,
        isError,
    } = useCompetitions();

    const items: Competition[] = result.items ?? [];

    // Buckets
    const openComps = useMemo(
        () => items.filter((c) => c.status === "OPEN"),
        [items]
    );
    const passedComps = useMemo(
        () => items.filter((c) => c.status === "CLOSED" || c.status === "WINNER_ANNOUNCED"),
        [items]
    );

    const viewId = searchParams.get("view") ?? undefined;
    const selected = useMemo(
        () => (viewId ? items.find((c) => c.id === viewId) ?? null : null),
        [viewId, items]
    );
    const modalOpen = Boolean(selected);

    const openDetails = (id: string) => {
        setParam((p) => {
            p.set("view", id);
        }, {replace: true});
    };

    const closeDetails = () => {
        setParam((p) => {
            p.delete("view");
        }, {replace: true});
    };

    return (
        <>
            <MetaTags
                title={"Kosmos 94.1 - Kompetisies"}
                description={"Neem deel aan ons opwindende kompetisies en wen wonderlike pryse!"}
                url={"/kompetisies"}
            />

            <HeroHeader
                title="Kompetisies"
                subtitle="Neem deel aan ons opwindende kompetisies en wen wonderlike pryse!"
                imageAlt="Kompetisies"
            />

            <section className="py-16 sm:py-22">
                <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    {isLoading && <LoadingGridItems/>}

                    {isError && (
                        <p className="text-sm text-red-600">
                            Kon nie kompetisies laai nie. Probeer asseblief weer.
                        </p>
                    )}

                    {!isLoading && !isError && openComps.length === 0 && passedComps.length === 0 && (
                        <NotAvailable title="Geen aktiewe kompetisies"/>
                    )}

                    {/* Active */}
                    {!isLoading && !isError && openComps.length > 0 && (
                        <>
                            <section>
                                <div className="mx-auto max-w-2xl text-center">
                                    <h2 className="text-4xl font-semibold tracking-tight text-blue-900 sm:text-5xl">
                                        Aktiewe Kompetisies
                                    </h2>
                                    <p className="mt-2 text-lg/8 text-gray-600">
                                        Neem deel aan ons opwindende kompetisies en wen wonderlike pryse!
                                    </p>
                                </div>

                                <div className="mx-auto mt-16">
                                    <div
                                        className="grid max-w-2xl grid-cols-1 gap-x-8 gap-y-10 lg:mx-0 lg:max-w-none lg:grid-cols-3">
                                        {openComps.map((c) => (
                                            <CompetitionCard key={c.id} comp={c} onClick={() => openDetails(c.id)}/>
                                        ))}
                                    </div>
                                </div>
                            </section>
                        </>
                    )}

                    {/* Past */}
                    {!isLoading && !isError && passedComps.length > 0 && (
                        <>
                            <section className="py-16">
                                <div className="mx-auto max-w-2xl text-center">
                                    <h2 className="text-4xl font-semibold tracking-tight text-blue-900 sm:text-5xl">
                                        Verlede Kompetisies
                                    </h2>
                                    <p className="mt-2 text-lg/8 text-gray-600">
                                        Kyk na ons verlede kompetisies.
                                    </p>
                                </div>

                                <div className="mx-auto mt-16">
                                    <div
                                        className="grid max-w-2xl grid-cols-1 gap-x-8 gap-y-10 lg:mx-0 lg:max-w-none lg:grid-cols-3">
                                        {passedComps.map((c) => (
                                            <CompetitionCard key={c.id} comp={c} onClick={() => openDetails(c.id)}/>
                                        ))}
                                    </div>
                                </div>
                            </section>
                        </>
                    )}
                </div>
            </section>

            <CompetitionDetailsDialog open={modalOpen} initial={selected} onClose={closeDetails}/>
        </>
    );
}
